import type { MediaOptions } from 'js/src/client';
import { MusicRecommendationModeEnum, WorkspaceMediaModeEnum } from 'js/src/client';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import Cookies from 'js-cookie';

type MediaRecommendationPreferencesCookie = MediaOptions & {
    music_recommendation_mode?: MusicRecommendationModeEnum;
    // Backward compatibility
    recommend_from_starred_music?: boolean;
};

const COOKIE_KEY = 'media_recommendation_preferences';
const COOKIE_EXPIRY_DAYS = 365; // 1 year

export function saveMediaRecommendationPreferencesToCookie(preferences: MediaOptions): void {
    try {
        const cookieValue = JSON.stringify(preferences);
        Cookies.set(COOKIE_KEY, cookieValue, { expires: COOKIE_EXPIRY_DAYS });
    } catch (error) {
        ErrorReporter.capture(error);
    }
}

export function loadMediaRecommendationPreferencesFromCookie(): MediaRecommendationPreferencesCookie | null {
    try {
        const cookieValue = Cookies.get(COOKIE_KEY);
        if (!cookieValue) {
            return null;
        }

        const parsed = JSON.parse(cookieValue);
        if (typeof parsed !== 'object' || parsed === null) {
            return null;
        }

        const isDamAvailable = Flags.canSeeDamTab();

        const preferences: MediaRecommendationPreferencesCookie = {
            source_media_enabled:
                typeof parsed.source_media_enabled === 'boolean'
                    ? parsed.source_media_enabled
                    : true,
            stock_media_enabled:
                typeof parsed.stock_media_enabled === 'boolean' ? parsed.stock_media_enabled : true,
            stock_icons_enabled:
                typeof parsed.stock_icons_enabled === 'boolean' ? parsed.stock_icons_enabled : true,
            dam_media_enabled:
                typeof parsed.dam_media_enabled === 'boolean'
                    ? parsed.dam_media_enabled
                    : isDamAvailable,
            workspace_media_mode:
                parsed.workspace_media_mode === WorkspaceMediaModeEnum.OFF ||
                parsed.workspace_media_mode === WorkspaceMediaModeEnum.FOLDERS ||
                parsed.workspace_media_mode === WorkspaceMediaModeEnum.ALL
                    ? parsed.workspace_media_mode
                    : WorkspaceMediaModeEnum.OFF,
            workspace_folder_ids: Array.isArray(parsed.workspace_folder_ids)
                ? parsed.workspace_folder_ids.filter((id: unknown) => typeof id === 'number')
                : [],
            media_guidance: typeof parsed.media_guidance === 'string' ? parsed.media_guidance : '',
            minimum_stock_suitability_score:
                typeof parsed.minimum_stock_suitability_score === 'number'
                    ? parsed.minimum_stock_suitability_score
                    : null,
            music_recommendation_mode: (() => {
                // First check for new enum value
                if (
                    parsed.music_recommendation_mode === MusicRecommendationModeEnum.RECOMMENDED ||
                    parsed.music_recommendation_mode === MusicRecommendationModeEnum.STARRED ||
                    parsed.music_recommendation_mode === MusicRecommendationModeEnum.OFF
                ) {
                    return parsed.music_recommendation_mode;
                }
                // Backward compatibility: convert old boolean to enum
                if (typeof parsed.recommend_from_starred_music === 'boolean') {
                    return parsed.recommend_from_starred_music
                        ? MusicRecommendationModeEnum.STARRED
                        : MusicRecommendationModeEnum.RECOMMENDED;
                }
                if (typeof parsed.use_starred_music === 'boolean') {
                    return parsed.use_starred_music
                        ? MusicRecommendationModeEnum.STARRED
                        : MusicRecommendationModeEnum.RECOMMENDED;
                }
                return MusicRecommendationModeEnum.RECOMMENDED;
            })(),
        };

        return preferences;
    } catch (error) {
        ErrorReporter.capture(error);
        return null;
    }
}
